/**
 * appVersion — has a newer build of the app been deployed since this tab loaded?
 *
 * REVIEW-2 (Michael, 2026-10-29: pages "refresh" and the rail "goes away"). A tab
 * opened before a deploy still asks for the old code files; Railway serves only
 * the new build, so a page whose file changed 404s. The app used to reload the
 * whole screen there. Now it asks this module whether a new build is live, and
 * if so offers "A new version of the app is ready — Refresh" instead.
 *
 * How it knows, with nothing baked in at build time: the tab's own entry script
 * is `/static/js/main.<hash>.js`. The live `index.html` names the live build's
 * entry script. If the live page no longer names ours, a new build is live.
 * (The dev server's `bundle.js` has no hash, so this always answers "no" there.)
 */

const MAIN_RE = /\/static\/js\/main\.[0-9a-f]+\.js/;

/** This tab's own entry script, e.g. `/static/js/main.883615b3.js`, or null in dev. */
export function currentMainScript(): string | null {
  if (typeof document === 'undefined') return null;
  for (const s of Array.from(document.scripts)) {
    const m = (s.getAttribute('src') || '').match(MAIN_RE);
    if (m) return m[0];
  }
  return null;
}

/** True when the live index.html names a different entry script than this tab runs. */
export async function isNewVersionLive(): Promise<boolean> {
  const mine = currentMainScript();
  if (!mine) return false;
  try {
    const res = await fetch('/', { cache: 'no-store', credentials: 'same-origin' });
    if (!res.ok) return false;
    const html = await res.text();
    const live = html.match(MAIN_RE);
    return !!live && live[0] !== mine;
  } catch {
    return false; // offline or blocked: not evidence of a new build
  }
}

export const NEW_VERSION_EVENT = 'tpl:new-version';
let announced = false;

/** Tell the shell (NewVersionWatcher) once per tab that a new build is live. */
export function announceNewVersion(): void {
  if (announced || typeof window === 'undefined') return;
  announced = true;
  window.dispatchEvent(new CustomEvent(NEW_VERSION_EVENT));
}

/** Test seam: forget that the prompt was shown. */
export function resetNewVersionAnnouncement(): void {
  announced = false;
}
